import Skeleton from "react-loading-skeleton";
import { JSX, useEffect, useState } from "react";
import { SelectFieldComponent } from "../../../../common/components/SelectFieldComponent";
import { FileInputFieldComponent } from "../FormComponents/FileInputFieldComponent";
import { SubmitButtonComponent } from "../FormComponents/SubmitButtonComponent";
import { getQualificationOptions } from "../../../../common/utils/selectOptions/getQualificationOptions";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { useCreateCertificateMutation } from "../../../../common/api/certificate";
import { useGetQualificationsQuery } from "../../../../common/api/qualification";
import {
    setFormValues,
    setIsPostCreateCertificateSuccess,
} from "../../CreateCertificateModalSlice";
import { QualificationInterface } from "../../../../common/interfaces/QualificationInterface";
import { handleCreateCertificate } from "../../utils/handleCreateCertificate";
import { useEstablishmentId } from "../../../../common/utils/useEstablishmentId";

export const OutsourcedForm = (): JSX.Element => {
    const dispatch = useAppDispatch();
    const establishmentId = useEstablishmentId();
    const [file, setFile] = useState<File | null>(null);
    const { formValues } = useAppSelector(
        (state) => state.createCertificateModal,
    );

    const [
        postCreateCertificate,
        {
            isLoading: isPostCreateCertificateLoading,
            isSuccess: isPostCreateCertificateSuccess,
        },
    ] = useCreateCertificateMutation();

    const { data: qualifications, isLoading: isQualificationsLoading } =
        useGetQualificationsQuery({
            items_per_page: 9999,
        });

    useEffect(() => {
        if (!isPostCreateCertificateSuccess) {
            dispatch(setIsPostCreateCertificateSuccess(false));
        }
    }, [isPostCreateCertificateSuccess, dispatch]);

    // Zet automatisch de ingangsdatum (valid_at) op vandaag bij uitbesteedde certificaten
    useEffect(() => {
        if (!formValues?.validAt) {
            const d = new Date();
            const yyyy = d.getFullYear();
            const mm = String(d.getMonth() + 1).padStart(2, "0");
            const dd = String(d.getDate()).padStart(2, "0");
            const today = `${yyyy}-${mm}-${dd}`;

            dispatch(
                setFormValues({
                    ...formValues,
                    validAt: today,
                }),
            );
        }
    }, [formValues, dispatch]);

    if (isQualificationsLoading) {
        return <Skeleton count={5} borderRadius={"0.8rem"} height={"5.5rem"} />;
    }

    return (
        <form
            className={"c-popup__content__body"}
            onSubmit={(event) => {
                event.preventDefault();
                if (!isPostCreateCertificateLoading) {
                    handleCreateCertificate({
                        formValues,
                        file,
                        postCreateCertificate,
                        dispatch,
                        isEstablishmentCertificate: true,
                        establishmentId,
                        isOutsourced: true,
                    });
                }
            }}
        >
            <SelectFieldComponent
                id={1}
                options={getQualificationOptions(qualifications?.data)}
                onChange={(newValue) => {
                    const selectedQualification = qualifications?.data?.find(
                        (item: QualificationInterface) =>
                            item.id.toString() === newValue.value,
                    );

                    dispatch(
                        setFormValues({
                            ...formValues,
                            qualification: {
                                ...formValues?.qualification,
                                selectedId: newValue.value,
                                selectedValue: newValue.label,
                                validityPeriod:
                                    selectedQualification?.validity_period ??
                                    null,
                                validityType:
                                    selectedQualification?.validity_type ??
                                    null,
                                fixedValidUntil:
                                    selectedQualification?.fixed_valid_until ??
                                    null,
                            },
                        }),
                    );
                }}
                value={{
                    value: formValues?.qualification?.selectedId || "",
                    label: formValues?.qualification?.selectedValue || "",
                }}
                errorMessage={"Kwalificatie is verplicht"}
                placeHolder={"Zoeken..."}
                title={"Selecteer kwalificatie"}
                showValidityPeriod={true}
                validityPeriod={formValues?.qualification?.validityPeriod}
                validityType={formValues?.qualification?.validityType}
                fixedValidUntil={formValues?.qualification?.fixedValidUntil}
                showNotice={!formValues?.qualification?.selectedId}
                noticeText={
                    'Staat jouw kwalificatie niet tussen de resultaten? Neem contact op met onze servicedesk via <a href="tel:09006611111">0900 - 66 11 11 1</a>'
                }
            />

            <FileInputFieldComponent
                id={5}
                formValues={formValues}
                dispatch={dispatch}
                setFile={setFile}
            />

            <SubmitButtonComponent
                isPostCreateCertificateLoading={isPostCreateCertificateLoading}
            />
        </form>
    );
};
